import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { settingsPath } from '../app/routes';
import { LayoutMode, useLayoutMode } from '../hooks/useMediaQuery';
import { useRouteView, ViewKind } from '../hooks/useRouteView';
import { GearIcon } from '../ui/icons';
import { DateNav } from './DateNav';
import { DayStats } from './DayStats';
import { HistoryButton } from './HistoryButton';
import { ExportHint } from './ExportHint';
import { useDayUi } from '../features/day/dayUiStore';
import { SyncIndicator } from './SyncStatusDot';
import styles from './TopBar.module.css';
import { UndoButtons } from './UndoButtons';
import { ViewSwitcher } from './ViewSwitcher';

/** Fixed 56px bar: views, date navigation, undo/redo, today's totals, help and settings. */
export function TopBar() {
  const { t } = useTranslation();
  const view = useRouteView();
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  const toggleHelp = useDayUi((state) => state.toggleHelp);
  // On a phone the first row is full: the history button moves to the date row when there is one.
  const hasDateNav =
    view.date !== null && (view.kind === ViewKind.Day || view.kind === ViewKind.Week || view.kind === ViewKind.Month);
  const historyInDateRow = narrow && hasDateNav;
  return (
    <header className={styles.bar} data-testid="top-bar">
      <ViewSwitcher />
      <DateNav trailing={historyInDateRow ? <HistoryButton /> : null} />
      <div className={styles.spacer} />
      {view.kind === ViewKind.Day && view.date !== null && !narrow ? <DayStats date={view.date} /> : null}
      <ExportHint />
      <UndoButtons />
      {historyInDateRow ? null : <HistoryButton />}
      <SyncIndicator />
      <button
        type="button"
        className={`${styles.iconButton} ${styles.helpButton}`}
        aria-label={t('help.open')}
        title={t('help.open')}
        data-testid="gesture-help-button"
        onClick={toggleHelp}
      >
        ?
      </button>
      <Link
        to={settingsPath()}
        className={styles.iconButton}
        aria-label={t('nav.settings')}
        title={t('nav.settings')}
      >
        <GearIcon />
      </Link>
    </header>
  );
}
